import { CompaniesService } from 'src/app/services/companies.service';
import { LinkedInService } from 'src/app/services/linkedin.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { OutreachService } from 'src/app/services/outreach.service';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { StrategyRouteChipComponent } from 'src/app/components/strategy-route-chip/strategy-route-chip.component';
import { Subject, takeUntil, tap } from 'rxjs';
import { SelectableCompany } from 'src/app/models/company.model';

export interface EmailSequence {
  id: number;
  sequenceId: string;
  prospectListId: string;
  name: string;
  description: string;
  target: string;
  steps: number;
  sequenceMedium: string;
  metrics: any;
  openRate: number;
  replyRate: number;
  progress: number;
  status: 'active' | 'paused' | 'draft' | 'completed';
  createdAt: Date;
  providerCampaignId: string;
  provider: string;
  numSteps: number;
  sender: string;
  /** Owning company (the sequences table's partition key). */
  companyId?: string;
}

interface EmailMetrics {
  // Backend stores metrics in camelCase
  totalOpened: number;
  totalReplied: number;
  totalBounced: number;
  totalSent: number;
  totalMeetings: number;
  totalProspects: number;
  // Legacy fields for backward compatibility
  open_count?: number;
  open_count_unique?: number;
  link_click_count?: number;
  reply_count?: number;
  bounced_count?: number;
  unsubscribed_count?: number;
  completed_count?: number;
  emails_sent_count?: number;
  total_opportunities?: number;
  total_opportunity_value?: number;
  total_interested?: number;
  total_meeting_booked?: number;
  total_meeting_completed?: number;
  total_closed?: number;
}

interface EmailCampaignInfo {
  campaign_name: string;
  campaign_id: string;
  campaign_status: number;
  leads_count: number;
  contacted_count: number;
  open_count: number;
  reply_count: number;
  link_click_count: number;
  bounced_count: number;
  emails_sent_count: number;
  total_opportunities: number;
  total_opportunity_value: number;
}

interface LinkedInMetrics {
  profileViews: number;
  postLikes: number;
  follows: number;
  messagesSent: number;
  totalMessageStarted: number;
  totalMessageReplies: number;
  inmailMessagesSent: number;
  totalInmailStarted: number;
  totalInmailReplies: number;
  connectionsSent: number;
  connectionsAccepted: number;
  messageReplyRate: number;
  inMailReplyRate: number;
  connectionAcceptanceRate: number;
}

@Component({
  selector: 'app-sequence-metrics',
  templateUrl: './sequence-metrics.component.html',
  styleUrls: ['./sequence-metrics.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    PanelModule,
    DividerModule,
    SkeletonModule,
    TooltipModule,
    ToastModule,
    StrategyRouteChipComponent
  ],
  providers: [MessageService]
})
export class SequenceMetricsComponent implements OnInit, OnDestroy {
  currentCompany: SelectableCompany | null = null;
  private destroy$ = new Subject<void>();
  sequenceId: string = '';
  companyId: string = '';
  platformType: 'email' | 'linkedin' = 'email';
  loading: boolean = true;
  sequence: EmailSequence | null = null;

  // Email metrics data
  emailMetrics: EmailMetrics | null = null;
  emailCampaignInfo: EmailCampaignInfo | null = null;

  // LinkedIn metrics data
  linkedinMetrics: LinkedInMetrics | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private linkedInService: LinkedInService,
    private emailOutReachService: OutreachService,
    private messageService: MessageService,
    private companiesService: CompaniesService
  ) { }

  ngOnInit() {
    this.sequenceId = this.route.snapshot.params['id'];
    this.companyId = this.route.snapshot.params['companyId'];
    this.determinePlatformType();
    this.setupCompanySubscription();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }
  private setupCompanySubscription(): void {
    // Use companyId from URL if available, otherwise use selected company
    if (this.companyId) {
      // Find the company by ID and set it as current
      const company = this.companiesService.getCompanyById(this.companyId);
      if (company) {
        this.currentCompany = company;
        
        this.loadSequenceData();
      } else {
        console.warn('Company not found for ID:', this.companyId);
        this.fallbackToSelectedCompany();
      }
    } else {
      this.fallbackToSelectedCompany();
    }
  }

  private fallbackToSelectedCompany(): void {
    // Subscribe to company changes and reload data when company changes
    this.companiesService.selectedCompany$
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe(async company => {
        if (company && company.companyId) {
          // Only load if this is a different company
          if (company.companyId !== this.currentCompany?.companyId) {
            console.log('Company changed from', this.currentCompany?.companyId, 'to', company.companyId);
            this.currentCompany = company;
            this.companyId = company.companyId;
            await this.loadSequenceData();
          }
        } else {
          console.log('No valid company received:', company);
          this.currentCompany = null;
        }
      });
  }

  private determinePlatformType() {
    const url = this.router.url;
    if (url.includes('linkedin')) {
      this.platformType = 'linkedin';
    } else if (url.includes('email')) {
      this.platformType = 'email';
    } else {
      this.platformType = 'email';
    }
  }

  private async loadSequenceData() {
    await this.loadSequence();
    if (this.platformType === 'email') {
       this.emailMetrics = await this.loadEmailMetrics();
    } else {
      this.linkedinMetrics = await this.loadLinkedInMetrics();
    }
    this.loading = false;

  }

  private async loadSequence() {
    if (this.platformType == 'email') {
      const result= await this.emailOutReachService.getEmailSequence(this.sequenceId, this.companyId);
      //@ts-ignore
      this.sequence = result.data.sequence;
    } else if (this.platformType == 'linkedin') {
      try {
        const result = await this.linkedInService.getLinkedInSequence(this.sequenceId, this.companyId);
        this.sequence = result.data.sequence;

      } catch (e) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data'
        });
      }
    }
  }

  private loadEmailMetrics() {
    return this.sequence?.metrics;
  }

  private loadLinkedInMetrics() {
    return this.sequence?.metrics;
  }

  // Navigation method
  goBack() {
    if (this.platformType === 'linkedin') {
      this.router.navigate(['/linkedin'], { queryParamsHandling: 'merge' });
    } else {
      this.router.navigate(['/email-outreach'], { queryParamsHandling: 'merge' });
    }
  }

  // Status utility method
  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' {
    if (!this.sequence) return 'info';
    switch (this.sequence.status) {
      case 'active': return 'success';
      case 'paused': return 'warn';
      case 'draft': return 'info';
      case 'completed': return 'info';
      default: return 'info';
    }
  }

  // Metric calculation methods
  getConnectionRate(): number {
    if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.connectionAcceptanceRate * 100;
    }
    return this.sequence?.openRate || 0;
  }

  getResponseRate(): number {
    if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.messageReplyRate * 100;
    }
    return this.sequence?.replyRate || 0;
  }

  getMeetingConversionRate(): number {
    if (this.platformType === 'email' && this.emailMetrics) {
      const meetings = this.emailMetrics.totalMeetings || this.emailMetrics.total_meeting_booked || 0;
      const replies = this.emailMetrics.totalReplied || this.emailMetrics.reply_count || 0;
      return replies > 0 ? (meetings / replies) * 100 : 0;
    } else if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      // Assuming meetings from LinkedIn responses - you can adjust this calculation
      return 0; // From screenshot
    }
    return 0;
  }

  getTotalSent(): number {
    if (this.platformType === 'email' && this.emailMetrics) {
      return this.emailMetrics.totalSent || this.emailMetrics.emails_sent_count || 0;
    } else if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.connectionsSent || 0;
    }
    return 0; // Default fallback
  }

  getTotalMessagesSent(): number {
    if (this.platformType === 'email' && this.emailMetrics) {
      return this.emailMetrics.totalSent || this.emailMetrics.emails_sent_count || 0;
    } else if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.messagesSent || 0;
    }
    return 0; // Default fallback
  }

  getConnections(): number {
    if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.connectionsAccepted;
    } else if (this.platformType === 'email' && this.emailMetrics) {
      return this.emailMetrics.totalOpened || this.emailMetrics.open_count || 0;
    }
    return 0; // Default fallback
  }

  getResponses(): number {
    if (this.platformType === 'linkedin' && this.linkedinMetrics) {
      return this.linkedinMetrics.totalMessageReplies;
    } else if (this.platformType === 'email' && this.emailMetrics) {
      return this.emailMetrics.totalReplied || this.emailMetrics.reply_count || 0;
    }
    return 0; // Default fallback
  }

  getMeetings(): number {
    if (this.platformType === 'email' && this.emailMetrics) {
      return this.emailMetrics.totalMeetings || this.emailMetrics.total_meeting_booked || 0;
    }
    // For LinkedIn, you might need to calculate this differently
    // or get it from a different source
    return 0; // Default fallback
  }

  // Formatting utility methods
  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }

  capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}